import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer
} from "react";
import { useTranslation } from "utilities/i18n";
import { Layout } from "views/Main/Layouts";
import {
  getColumns,
  sortTableData,
  TableBody,
  TableBulkItemHeader,
  TableBulkItemRow,
  TableHeaderItem,
  TableSearchEdit
} from "collections/Table/Table";
import { Loader, NoResultsIndicatorAnimation } from "elements";
import { SortState } from "store/filters";
import { AuditEvent, useGetAuditEventList } from "../useAuditEvents";
import { toLicenseDateLocaleDateString } from "utilities/licenseServerUtils";
import {
  AuditEventListReducer,
  defaultAuditEventListState,
  updateDataLoaded,
  updatePageInfo,
  updateSearch,
  updateSelectedItems,
  updateSort
} from "./AuditEventListReducer";
import { TABLE_COLUMN_STATE, TABLES } from "store/userMeta";
import { useSelector } from "react-redux";
import { State } from "store";
import { generateTableHeaders } from "utilities/tableUtils";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";
import { handleNullFields } from "utilities/stringUtils";
import { generatePath, Link } from "polyrouter";

type AuditEventRowRowProps = {
  auditEvent: AuditEvent;
  shownColumns: Array<string>;
};

const { NAME: TABLE_NAME, COLUMNS, HIDDEN } = TABLES.CLARITI_AUDIT_EVENTS;

export const AuditEventRow: React.FC<AuditEventRowRowProps> = ({
  auditEvent,
  shownColumns
}) => {
  const {
    entitlementName,
    eventTime,
    eventTypeName,
    productName,
    recordId,
    userId,
    userName
  } = auditEvent;

  const getTableContent = (column: string) => {
    let content: ReactNode = null;
    switch (column) {
      case COLUMNS.ENTITLEMENT_NAME:
        content = handleNullFields(entitlementName);
        break;
      case COLUMNS.EVENT_TIME:
        content = toLicenseDateLocaleDateString(eventTime);
        break;
      case COLUMNS.EVENT_TYPE:
        content = (
          <Link
            to={generatePath(
              "/manage/licensing/clariti/audit-events/details/:recordId",
              { recordId }
            )}
            className="d-block"
          >
            {handleNullFields(eventTypeName)}
          </Link>
        );
        break;
      case COLUMNS.PRODUCT_NAME:
        content = handleNullFields(productName);
        break;
      case COLUMNS.USER_ID:
        content = handleNullFields(userId);
        break;
      case HIDDEN.USER_NAME:
        content = handleNullFields(userName);
        break;
    }

    return <td key={column}>{content}</td>;
  };

  return (
    <TableBulkItemRow checked={false} onCheck={() => {}} showChecks={false}>
      {shownColumns.map(getTableContent)}
    </TableBulkItemRow>
  );
};

export const AuditEventList: React.FC = () => {
  const { tenant } = useContext(SelectedTenantContext);
  const tenantId = tenant?.id ?? "";
  const { Content } = Layout;
  const { t } = useTranslation();

  const [
    { dataLoaded, sort, searchTerm, auditEventCount, auditEvents },
    dispatch
  ] = useReducer(AuditEventListReducer, defaultAuditEventListState);

  const {
    [TABLE_COLUMN_STATE.SHOWN]: shownColumns,
    [TABLE_COLUMN_STATE.HIDDEN]: hiddenColumns
  } = useSelector((state: State) => {
    return state.userMeta?.tables[TABLE_NAME] ?? {};
  });

  const { data, fetching, stale } = useGetAuditEventList({
    variables: {
      tenantId
    }
  });

  const formattedSearchTerm = searchTerm?.toLowerCase();

  // Since filtering is handled locally, we will automatically load all data so things work as expected
  const filteredData = !!formattedSearchTerm;

  // Tenant ID switch
  useEffect(() => {
    dispatch(updateDataLoaded(false));
    dispatch(updateSelectedItems([]));
  }, [tenantId]);

  const loading = fetching || stale;
  const auditEventsData = data;

  // updates the display after the data has been loaded
  useEffect(() => {
    if (!loading) {
      dispatch(updateDataLoaded(true));

      if (auditEventsData) {
        dispatch(
          updatePageInfo({
            auditEventCount: auditEventsData.totalCount,
            auditEvents: auditEventsData.edges.map(entry => {
              return { ...entry.node };
            })
          })
        );
      }
    }
  }, [loading, auditEventsData]);

  const setSort = useCallback((sort: SortState) => {
    dispatch(updateSort(sort));
  }, []);

  const setSearch = useCallback((search?: string) => {
    dispatch(updateSearch(search));
  }, []);

  // Sort audit events
  sortTableData(auditEvents, sort);

  const columnTranslations = {
    [COLUMNS.EVENT_TYPE]: t("EVENT_TYPE"),
    [COLUMNS.EVENT_TIME]: t("TIME"),
    [COLUMNS.USER_ID]: t("USER_ID"),
    [HIDDEN.USER_NAME]: t("USER_NAME"),
    [COLUMNS.ENTITLEMENT_NAME]: t("ENTITLEMENT_NAME"),
    [COLUMNS.PRODUCT_NAME]: t("PRODUCT_NAME")
  };

  const getTableHeaders = (column: string, title: string) => {
    switch (column) {
      case COLUMNS.EVENT_TYPE:
        break;
    }

    return (
      <TableHeaderItem
        fieldName={column}
        title={title}
        sort={{
          currentSort: sort,
          onSort: setSort
        }}
        key={column}
      />
    );
  };

  const shown = getColumns(shownColumns, columnTranslations);
  const hidden = getColumns(hiddenColumns, columnTranslations);

  // filters the data based on the specified search criteria
  const filteredAuditEvents = useMemo(() => {
    return formattedSearchTerm
      ? auditEvents.filter(
          auditEvent =>
            auditEvent?.eventTypeName
              ?.toLowerCase()
              .includes(formattedSearchTerm) ||
            auditEvent?.entitlementName
              ?.toLowerCase()
              .includes(formattedSearchTerm) ||
            auditEvent?.productName
              ?.toLowerCase()
              .includes(formattedSearchTerm) ||
            auditEvent?.userId?.toLowerCase().includes(formattedSearchTerm) ||
            auditEvent?.userName?.toLowerCase().includes(formattedSearchTerm)
        )
      : auditEvents;
  }, [formattedSearchTerm, auditEvents]);

  const displayCount = filteredData
    ? filteredAuditEvents.length
    : auditEventCount;

  return (
    <Content>
      <Loader loading={!dataLoaded}>
        {!loading && auditEventCount === 0 ? (
          <NoResultsIndicatorAnimation title={t("NO_AUDIT_EVENTS_FOUND")} />
        ) : (
          <>
            <div className="table-heading">
              <span className="metric">{displayCount}</span>
              <span className="title ml-5">
                {t("AUDIT_EVENTS", { count: displayCount })}
              </span>
            </div>
            <div className="grouping mb-15 table-actions">
              <TableSearchEdit
                search={{
                  onSearch: setSearch,
                  searchPlaceholder: t("SEARCH_AUDIT_EVENTS")
                }}
                edit={{
                  tableName: TABLE_NAME,
                  shownColumns: shown,
                  hiddenColumns: hidden
                }}
              />
            </div>
            <div className="table-container">
              <table className="table custom-table">
                <TableBulkItemHeader
                  checked={false}
                  onCheck={() => {}}
                  showChecks={false}
                >
                  {generateTableHeaders(shown, getTableHeaders)}
                </TableBulkItemHeader>
                <TableBody
                  loading={loading}
                  cols={shown.length}
                  accommodateScrollbar
                >
                  {filteredAuditEvents.map(auditEvent => {
                    return (
                      <AuditEventRow
                        key={auditEvent.recordId}
                        auditEvent={auditEvent}
                        shownColumns={shown.map(column => column.key)}
                      />
                    );
                  })}
                </TableBody>
              </table>
            </div>
          </>
        )}
      </Loader>
    </Content>
  );
};
